/**
 * Token-refresh scheduling, isolated from React so it can be unit-tested.
 *
 * Requirement 23.6: when the access token expires (or is about to), obtain a
 * new access token using the refresh token. This module decides *when* to
 * refresh and performs the refresh via the injected {@link AuthClient}, exposing
 * a small, deterministic surface that tests drive with a fake clock/timer.
 */
/** Default lead time before expiry at which a proactive refresh fires (ms). */
export const DEFAULT_REFRESH_SKEW_MS = 60_000;
const realTimers = {
    now: () => Date.now(),
    setTimeout: (handler, timeout) => setTimeout(handler, timeout),
    clearTimeout: (handle) => clearTimeout(handle),
};
/**
 * Computes the delay (ms) until a refresh should fire for a session, given the
 * current time and skew. Never negative — an already-expired (or within-skew)
 * token yields `0`, meaning "refresh immediately".
 */
export function computeRefreshDelay(session, now, skewMs = DEFAULT_REFRESH_SKEW_MS) {
    const fireAt = session.expiresAt - skewMs;
    return Math.max(0, fireAt - now);
}
/**
 * Returns `true` when the access token is expired or within `skewMs` of expiry,
 * i.e. a refresh is needed before using it. Used by the API token provider so
 * outgoing requests never carry a stale token (Req 23.6).
 */
export function isExpiredOrExpiring(session, now, skewMs = DEFAULT_REFRESH_SKEW_MS) {
    return now >= session.expiresAt - skewMs;
}
/**
 * Schedules and performs proactive token refreshes for the active session.
 *
 * Lifecycle:
 *   - `start(session)` schedules a refresh just before the token expires.
 *   - On fire, it calls `authClient.refreshSession(...)`, emits the new session
 *     via `onRefreshed`, and re-schedules from the new `expiresAt`.
 *   - `refreshNow()` forces an immediate refresh (used for on-demand refresh
 *     when an API call finds the token already expired).
 *   - `stop()` cancels any pending timer (e.g. on sign-out / unmount).
 */
export class TokenRefreshScheduler {
    authClient;
    skewMs;
    onRefreshed;
    onError;
    timers;
    handle = null;
    currentRefreshToken = null;
    /** De-dupes concurrent refreshes (scheduled + on-demand) onto one network call. */
    inFlight = null;
    constructor(options) {
        this.authClient = options.authClient;
        this.skewMs = options.skewMs ?? DEFAULT_REFRESH_SKEW_MS;
        this.onRefreshed = options.onRefreshed;
        this.onError = options.onError ?? (() => { });
        this.timers = options.timers ?? realTimers;
    }
    /** Begin scheduling refreshes for the given session. Cancels any prior schedule. */
    start(session) {
        this.currentRefreshToken = session.refreshToken;
        this.schedule(session);
    }
    /** Cancel any pending refresh and forget the refresh token. */
    stop() {
        if (this.handle !== null) {
            this.timers.clearTimeout(this.handle);
            this.handle = null;
        }
        this.currentRefreshToken = null;
    }
    /**
     * Force an immediate refresh using the current refresh token. Concurrent calls
     * share a single in-flight network request. Throws if no refresh token is set.
     */
    async refreshNow() {
        if (this.inFlight)
            return this.inFlight;
        const refreshToken = this.currentRefreshToken;
        if (!refreshToken) {
            throw new Error('No refresh token available; cannot refresh session.');
        }
        this.inFlight = (async () => {
            try {
                const next = await this.authClient.refreshSession(refreshToken);
                // A refresh may rotate the refresh token; keep the freshest one.
                this.currentRefreshToken = next.refreshToken;
                this.onRefreshed(next);
                this.schedule(next);
                return next;
            }
            catch (error) {
                this.onError(error);
                throw error;
            }
            finally {
                this.inFlight = null;
            }
        })();
        return this.inFlight;
    }
    schedule(session) {
        if (this.handle !== null) {
            this.timers.clearTimeout(this.handle);
            this.handle = null;
        }
        const delay = computeRefreshDelay(session, this.timers.now(), this.skewMs);
        this.handle = this.timers.setTimeout(() => {
            this.handle = null;
            void this.refreshNow().catch(() => {
                // Error already reported via onError; swallow to avoid unhandled rejection.
            });
        }, delay);
    }
}
//# sourceMappingURL=token-refresh.js.map